import { Lead, LeadEmail, LeadPhone, VehicleInterest } from '../../../../types'
import { LeadEditFormData, LeadEditFormattedValues } from '../../domain/interfaces'
import { formatCurrency, formatMileage, parseCurrency, parseMileage } from '../../../../utils/formatters'

export class LeadMapper {
 static toFormData(lead: Lead): LeadEditFormData {
 const leadPhones: LeadPhone[] = lead.phones && lead.phones.length > 0
 ? lead.phones
 : lead.phone
 ? [{ phone: lead.phone, type: 'personal', isPrimary: true, isWhatsApp: false }]
 : []

 const leadEmails: LeadEmail[] = lead.emails && lead.emails.length > 0
 ? lead.emails
 : lead.email
 ? [{ email: lead.email, type: 'personal', isPrimary: true }]
 : []

 const leadVehicleInterests: VehicleInterest[] = lead.vehicleInterests && lead.vehicleInterests.length > 0
 ? lead.vehicleInterests
 : lead.vehicleIds
 ? lead.vehicleIds.map((id, index) => ({ vehicleId: id, priority: index + 1, notes: '' }))
 : []

 return {
 name: lead.name || '',
 phones: leadPhones,
 emails: leadEmails,
 document: lead.document || '',
 cnh: lead.cnh || '',
 message: lead.message || '',
 source: lead.source || '',
 sellerId: lead.assignedTo || '',
 vehicleInterests: leadVehicleInterests,
 interest: lead.interest || {},
 notes: lead.notes || '',
 contactCount: lead.contactCount ?? null,
 engagement: lead.engagement || '',
 purchaseIntent: lead.purchaseIntent || '',
 usageType: lead.usageType || '',
 vehicleMoment: lead.vehicleMoment || '',
 hasTradeIn: Boolean(lead.hasTradeIn),
 tradeInVehicleDescription: lead.tradeInVehicleDescription || '',
 tradeInVehicles: Array.isArray(lead.tradeInVehicles) && lead.tradeInVehicles.length > 0
 ? lead.tradeInVehicles.map((vehicle) => ({
 brand: vehicle.brand || '',
 model: vehicle.model || '',
 version: vehicle.version || '',
 }))
 : lead.tradeInVehicleDescription
 ? [{ brand: '', model: lead.tradeInVehicleDescription, version: '' }]
 : [],
 tradeInEvaluationEligible: Boolean(lead.tradeInEvaluationEligible),
 tradeInEvaluationScore: lead.tradeInEvaluationScore ?? null,
 tradeInEstimatedValue: lead.tradeInEstimatedValue ?? null,
 tradeInEvaluationNotes: lead.tradeInEvaluationNotes || '',
 wantsFinancing: Boolean(lead.wantsFinancing),
 financingWithTradeIn: Boolean(lead.financingWithTradeIn),
 downPaymentAmount: lead.downPaymentAmount ?? null,
 downPaymentEntries: Array.isArray(lead.downPaymentEntries) && lead.downPaymentEntries.length > 0
 ? lead.downPaymentEntries.map((entry) => ({
 amount: typeof entry.amount === 'number' ? entry.amount : null,
 note: entry.note || '',
 }))
 : lead.downPaymentAmount !== null && lead.downPaymentAmount !== undefined
 ? [{ amount: lead.downPaymentAmount, note: '' }]
 : [],
 desiredInstallmentAmount: lead.desiredInstallmentAmount ?? null,
 approvedCreditAmount: lead.approvedCreditAmount ?? null,
 creditScore: lead.creditScore ?? null,
 financingNotes: lead.financingNotes || '',
 }
 }

 static toFormattedValues(lead: Lead): LeadEditFormattedValues {
 return {
 minPriceFormatted: lead.interest?.minPrice ? formatCurrency(lead.interest.minPrice) : '',
 maxPriceFormatted: lead.interest?.maxPrice ? formatCurrency(lead.interest.maxPrice) : '',
 minMileageFormatted: lead.interest?.minMileage ? formatMileage(lead.interest.minMileage) : '',
 maxMileageFormatted: lead.interest?.maxMileage ? formatMileage(lead.interest.maxMileage) : ''
 }
 }

 static toApiData(
 formData: LeadEditFormData,
 formattedValues: LeadEditFormattedValues
 ): any {
 
 const apiData: any = {
 name: formData.name,
 phones: formData.phones.map(p => ({
 ddi: p.ddi || '+55',
 ddd: p.ddd?.replace(/\D/g, '') || '',
 phone: p.phone?.replace(/\D/g, '') || '',
 type: p.type,
 isPrimary: p.isPrimary,
 isWhatsApp: p.isWhatsApp
 }))
 }

 if (formData.emails.length > 0) {
 apiData.emails = formData.emails.map(e => ({
 email: e.email,
 type: e.type,
 isPrimary: e.isPrimary
 // Remove campos de sistema: id, leadId, createdAt, updatedAt
 }))
 }

 if (formData.document) {
 apiData.document = formData.document
 }

 if (formData.cnh) {
 apiData.cnh = formData.cnh
 }

 if (formData.message) {
 apiData.message = formData.message
 }

 if (formData.source) {
 apiData.source = formData.source
 }

 if (formData.sellerId) {
 apiData.assignedTo = formData.sellerId
 }

 if (formData.vehicleInterests.length > 0) {
 apiData.vehicleInterests = formData.vehicleInterests
 }

 if (formData.notes) {
 apiData.notes = formData.notes
 }

 if (formData.contactCount !== null) apiData.contactCount = formData.contactCount
 if (formData.engagement) apiData.engagement = formData.engagement
 if (formData.purchaseIntent) apiData.purchaseIntent = formData.purchaseIntent
 if (formData.usageType) apiData.usageType = formData.usageType
 if (formData.vehicleMoment) apiData.vehicleMoment = formData.vehicleMoment
 apiData.hasTradeIn = formData.hasTradeIn
 if (formData.tradeInVehicleDescription) {
   apiData.tradeInVehicleDescription = formData.tradeInVehicleDescription
 }
apiData.tradeInVehicles = formData.tradeInVehicles
  .filter(vehicle => vehicle.brand || vehicle.model || vehicle.version)
  .map(vehicle => ({
    brand: vehicle.brand || undefined,
    model: vehicle.model || undefined,
    version: vehicle.version || undefined,
  }))
if (!apiData.tradeInVehicleDescription && apiData.tradeInVehicles.length > 0) {
  const firstVehicle = apiData.tradeInVehicles[0]
  apiData.tradeInVehicleDescription = [firstVehicle.brand, firstVehicle.model, firstVehicle.version]
    .filter(Boolean)
    .join(' ')
}
 apiData.tradeInEvaluationEligible = formData.tradeInEvaluationEligible
 if (formData.tradeInEvaluationScore !== null) {
   apiData.tradeInEvaluationScore = formData.tradeInEvaluationScore
 }
 if (formData.tradeInEstimatedValue !== null) {
   apiData.tradeInEstimatedValue = formData.tradeInEstimatedValue
 }
 if (formData.tradeInEvaluationNotes) {
   apiData.tradeInEvaluationNotes = formData.tradeInEvaluationNotes
 }
 apiData.wantsFinancing = formData.wantsFinancing
 apiData.financingWithTradeIn = formData.financingWithTradeIn
 if (formData.downPaymentAmount !== null) {
   apiData.downPaymentAmount = formData.downPaymentAmount
 }
apiData.downPaymentEntries = formData.downPaymentEntries
  .filter(entry => entry.amount !== null)
  .map(entry => ({
    amount: entry.amount,
    note: entry.note || undefined,
  }))
if ((apiData.downPaymentAmount === undefined || apiData.downPaymentAmount === null) && apiData.downPaymentEntries.length > 0) {
  apiData.downPaymentAmount = apiData.downPaymentEntries[0].amount
}
 if (formData.desiredInstallmentAmount !== null) {
   apiData.desiredInstallmentAmount = formData.desiredInstallmentAmount
 }
 if (formData.approvedCreditAmount !== null) {
   apiData.approvedCreditAmount = formData.approvedCreditAmount
 }
 if (formData.creditScore !== null) {
   apiData.creditScore = formData.creditScore
 }
 if (formData.financingNotes) {
   apiData.financingNotes = formData.financingNotes
 }

 const preferences: any = {}

 // Map UI interest keys to API LeadPreferencesDto keys
 if (formData.interest.category) preferences.category = formData.interest.category
 if (formData.interest.type) preferences.type = formData.interest.type
 if (formData.interest.segmentType) preferences.segmentType = formData.interest.segmentType
 if (formData.interest.condition) preferences.condition = formData.interest.condition
 if (formData.interest.fuelType) preferences.fuelType = formData.interest.fuelType
 if (formData.interest.transmission) preferences.transmission = formData.interest.transmission
 if (formData.interest.steeringType) preferences.steeringType = formData.interest.steeringType
 if (formData.interest.color) preferences.color = formData.interest.color
 if (formData.interest.doors) preferences.doors = formData.interest.doors
 if (formData.interest.seats) preferences.seats = formData.interest.seats
 if (formData.interest.armorLevel) preferences.armorLevel = formData.interest.armorLevel
 if (formData.interest.specialCategory) preferences.specialCategory = formData.interest.specialCategory
 if (formData.interest.minYear) preferences.yearMin = formData.interest.minYear
 if (formData.interest.maxYear) preferences.yearMax = formData.interest.maxYear
 if (formData.interest.minMileage) preferences.mileageMin = formData.interest.minMileage
 if (formData.interest.maxMileage) preferences.mileageMax = formData.interest.maxMileage
 if (formData.interest.characteristics && formData.interest.characteristics.length > 0) {
 preferences.desiredCharacteristics = formData.interest.characteristics
 }
 if (formData.interest.features && formData.interest.features.length > 0) {
 preferences.desiredFeatures = formData.interest.features
 }

 if (formattedValues.minPriceFormatted) {
 preferences.priceMin = parseCurrency(formattedValues.minPriceFormatted)
 }
 if (formattedValues.maxPriceFormatted) {
 preferences.priceMax = parseCurrency(formattedValues.maxPriceFormatted)
 }

 if (formattedValues.minMileageFormatted) {
 preferences.mileageMin = parseMileage(formattedValues.minMileageFormatted)
 }
 if (formattedValues.maxMileageFormatted) {
 preferences.mileageMax = parseMileage(formattedValues.maxMileageFormatted)
 }

 const hasPreferences = Object.keys(preferences).some(key => {
 const value = preferences[key]
 if (Array.isArray(value)) return value.length > 0
 return value !== undefined && value !== null && value !== ''
 })

 if (hasPreferences) {
 apiData.preferences = preferences
 }

 return apiData
 }
}

